import * as React from 'react'
import CloseIcon from '@/assets/icons_new/CloseGray.svg'
import ModalLayout from '@/modules/drivers/modals/shared/ModalLayout'
import { FaIcon } from '@/lib/fa'
import {
  faTruckRegular,
  faHammerRegular,
  faIdBadgeRegular,
  faBedFrontRegular,
  faQuestionCircleRegular,
} from '@/lib/fa'
import { IconProp } from '@fortawesome/fontawesome-svg-core'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'

type WeeklyLegendModalProps = {
  isOpen: boolean
  onClose: () => void
}

const ActivitySwatch: React.FC<{ color: string; icon: IconProp }> = ({ color, icon }) => (
  <div
    className="tw-flex tw-shrink-0 tw-items-center tw-justify-center"
    style={{
      width: 28,
      height: 16,
      background: color,
      borderRadius: 4,
    }}
  >
    <FaIcon icon={icon} className="tw-h-3 tw-w-3" width={10} height={10} />
  </div>
)

const RectSwatch: React.FC<{ color: string; radius?: number }> = ({ color, radius = 2 }) => (
  <div
    className="tw-shrink-0"
    style={{
      width: 28,
      height: 12,
      background: color,
      borderRadius: radius,
    }}
  />
)

const CardInsertSwatch: React.FC = () => (
  <svg width="28" height="18" viewBox="0 0 28 18" className="tw-shrink-0">
    <line x1="14" y1="7" x2="14" y2="18" stroke="#C0C0C0" strokeWidth="2" />
    <path d="M 9,0 14,7 19,0" fill="#FF6A88" />
  </svg>
)

/** PlaceBlock-style: arrow-shaped flag with vertical needle line */
const PlaceSwatch: React.FC = () => (
  <svg width="28" height="18" viewBox="0 0 28 18" className="tw-shrink-0">
    <line x1="14" y1="10" x2="14" y2="18" stroke="#E1E1EB" strokeWidth="2" />
    <polygon points="2,0 22,0 28,5 22,10 2,10" fill="#E1E1EB" />
    <text x="12" y="8" textAnchor="middle" fill="#555" fontSize="7" fontWeight="600">DE</text>
  </svg>
)

const WeekRecompenseSwatch: React.FC = () => (
  <svg width="28" height="18" viewBox="0 0 28 18" className="tw-shrink-0">
    <rect x="0" y="13" width="28" height="5" rx="1" fill="#7280FF" />
    <path d="M 8,13 8,10 18,7 18,3" fill="none" stroke="#7280FF" strokeWidth="1.5" />
    <rect x="14" y="0" width="12" height="11" rx="1" fill="#7280FF" />
    <text x="20" y="9" textAnchor="middle" fill="#fff" fontSize="8" fontWeight="500">1</text>
  </svg>
)

/** Manual entry: activity block with dark diagonal stripes, matching .activity CSS class */
const ManualEntrySwatch: React.FC = () => (
  <div
    className="tw-shrink-0"
    style={{
      width: 28,
      height: 16,
      background: '#75C5F0',
      borderRadius: 4,
      position: 'relative',
      overflow: 'hidden',
    }}
  >
    <div
      style={{
        position: 'absolute',
        inset: 0,
        opacity: 0.2,
        background: 'repeating-linear-gradient(45deg, rgba(0,0,0,0), rgba(0,0,0,0) 3px, rgba(0,0,0,0.2) 3px, rgba(0,0,0,1) 6px)',
      }}
    />
  </div>
)

/** Modified by user: activity block with white diagonal stripes, matching .activity-white CSS class */
const ModifiedByUserSwatch: React.FC = () => (
  <div
    className="tw-shrink-0"
    style={{
      width: 28,
      height: 16,
      background: '#75C5F0',
      borderRadius: 4,
      position: 'relative',
      overflow: 'hidden',
    }}
  >
    <div
      style={{
        position: 'absolute',
        inset: 0,
        opacity: 0.4,
        background: 'repeating-linear-gradient(45deg, rgba(255,255,255,0), rgba(255,255,255,0) 3px, rgba(255,255,255,0.2) 3px, rgba(255,255,255,1) 6px)',
      }}
    />
  </div>
)

/** DayActivity start-of-day: bordered box with time, matching DayActivityBlock.tsx drawStartDayInfo */
const DayStartSwatch: React.FC = () => (
  <svg width="34" height="14" viewBox="0 0 34 14" className="tw-shrink-0">
    <rect x="0.5" y="0.5" width="33" height="13" rx="2" fill="#fff" stroke="#3A3F48" strokeWidth="1.5" />
    <text x="4" y="11" fontSize="9" fontWeight="500" fill="#3A3F48">05:49</text>
  </svg>
)

/** DayActivity end-of-day: bordered box with time + Σ duration + red dot driving, matching DayActivityBlock.tsx drawEndDayInfo */
const DayEndSwatch: React.FC = () => (
  <svg width="100" height="14" viewBox="0 0 100 14" className="tw-shrink-0">
    <rect x="0.5" y="0.5" width="99" height="13" rx="2" fill="#fff" stroke="#3A3F48" strokeWidth="1.5" />
    <text x="4" y="11" fontSize="9" fontWeight="500" fill="#3A3F48">18:41</text>
    <text x="32" y="11" fontSize="9" fontWeight="500" fill="#343434">&Sigma;</text>
    <text x="39" y="11" fontSize="9" fontWeight="500" fill="#343434">12:52</text>
    <circle cx="68" cy="7" r="3.5" fill="#FD5C54" stroke="#343434" strokeWidth="1" />
    <text x="74" y="11" fontSize="9" fontWeight="500" fill="#343434">09:35</text>
  </svg>
)

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div className="tw-mb-4">
    <h3 className="tw-mb-2 tw-text-sm tw-font-semibold tw-text-gray-700">{title}</h3>
    <div className="tw-grid tw-grid-cols-2 tw-gap-x-6 tw-gap-y-1.5">
      {children}
    </div>
  </div>
)

const LegendRow: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div className="tw-flex tw-items-center tw-gap-2">
    {children}
    <span className="tw-text-xs tw-text-gray-600">{label}</span>
  </div>
)

export const WeeklyLegendModal: React.FC<WeeklyLegendModalProps> = ({ isOpen, onClose }) => {
  const { t } = useLocalize()
  if (!isOpen) return null
  return (
    <ModalLayout>
      <div className="tw-w-full tw-max-w-[600px] tw-rounded-lg tw-bg-white tw-p-4">
        <div className="tw-flex tw-items-center tw-justify-between tw-pb-5">
          <h2 className="tw-text-xl tw-font-semibold tw-text-content-amber">
            {t('driverModule.legend')}
          </h2>
          <img
            src={CloseIcon}
            onClick={onClose}
            alt="Close Icon"
            className="tw-h-5 tw-w-5 tw-cursor-pointer"
          />
        </div>

        <div className="tw-mb-5">
          <Section title={t('driverModule.legend-activities')}>
            <LegendRow label={t('driverModule.legend-driving')}><ActivitySwatch color="#FD5C54" icon={faTruckRegular} /></LegendRow>
            <LegendRow label={t('driverModule.legend-work')}><ActivitySwatch color="#F5AE31" icon={faHammerRegular} /></LegendRow>
            <LegendRow label={t('driverModule.legend-availability')}><ActivitySwatch color="#F1E834" icon={faIdBadgeRegular} /></LegendRow>
            <LegendRow label={t('driverModule.legend-rest')}><ActivitySwatch color="#75C5F0" icon={faBedFrontRegular} /></LegendRow>
            <LegendRow label={t('driverModule.legend-unknown')}><ActivitySwatch color="#C0C0C0" icon={faQuestionCircleRegular} /></LegendRow>
          </Section>

          <Section title={t('driverModule.legend-rests-breaks')}>
            <LegendRow label={t('driverModule.legend-break')}><RectSwatch color="#0094FF" radius={4} /></LegendRow>
            <LegendRow label={t('driverModule.legend-partial-break')}><RectSwatch color="#0094FF" radius={4} /></LegendRow>
            <LegendRow label={t('driverModule.legend-daily-rest')}><RectSwatch color="#D58CFF" /></LegendRow>
            <LegendRow label={t('driverModule.legend-daily-rest-partial')}><RectSwatch color="#B200FF" /></LegendRow>
            <LegendRow label={t('driverModule.legend-weekly-rest')}><RectSwatch color="#22B14C" radius={4} /></LegendRow>
            <LegendRow label={t('driverModule.legend-weekly-rest-reduced')}><RectSwatch color="#86F464" /></LegendRow>
            <LegendRow label={t('driverModule.legend-weekly-recompense')}><WeekRecompenseSwatch /></LegendRow>
          </Section>

          <Section title={t('driverModule.legend-other')}>
            <LegendRow label={t('driverModule.legend-card-insert')}><CardInsertSwatch /></LegendRow>
            <LegendRow label={t('driverModule.legend-manual-place')}><PlaceSwatch /></LegendRow>
            <LegendRow label={t('driverModule.legend-manual-entry')}><ManualEntrySwatch /></LegendRow>
            <LegendRow label={t('driverModule.legend-modified-by-user')}><ModifiedByUserSwatch /></LegendRow>
          </Section>

          <Section title={t('driverModule.legend-daily-info')}>
            <LegendRow label={t('driverModule.legend-day-start')}><DayStartSwatch /></LegendRow>
            <LegendRow label={t('driverModule.legend-day-end')}><DayEndSwatch /></LegendRow>
          </Section>
        </div>

        <div className="tw-flex tw-justify-end tw-pt-4">
          <button
            className="tw-rounded-lg tw-bg-secondary tw-p-3 tw-text-sm tw-font-medium tw-text-primary"
            onClick={onClose}
          >
            {t('common.close')}
          </button>
        </div>
      </div>
    </ModalLayout>
  )
}
